import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { stringIsNotEmpty } from '@trade-platform/ui-utils';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { auditTime, debounceTime, filter, first, withLatestFrom } from 'rxjs/operators';
import { DynamicFormStore } from '../../dynamic-form-store';
import {
    DynamicFormControlSetDirtyAction,
    DynamicFormSetDataAction
} from '../../dynamic-form-store/actions';
import { DynamicFormState } from '../../dynamic-form-store/model';
import { getDataPathByFieldConfig } from '../../dynamic-form-store/utils';
import { getHostPropertyClass } from '../../dynamic-form.constants';
import { DynamicFormHelper } from '../../dynamic-form.helper';
import { InputFieldConfig } from '@trade-platform/form-fields';
import { Field, FieldEvent } from '../field.interface';
import { handleServerValdationErrors } from '../../dynamic-form.utils';
import { AixFormErrorsPipe } from '../../../pipes/form-errors';
import { AixDataTestingDirective, AixInputComponent } from '@trade-platform/ui-components';
import { AixDynamicNextPendingFieldDirective } from '../../directives/dynamic-next-pending-field';

@Component({
    selector: 'aix-dynamic-input',
    styleUrls: ['./input.component.scss'],
    templateUrl: './input.component.html',
    standalone: true,
    imports: [
        AixInputComponent,
        AixDynamicNextPendingFieldDirective,
        AixDataTestingDirective,
        AixFormErrorsPipe
    ]
})
export class AixDynamicInputComponent implements Field, OnInit, OnDestroy {
    private helper = inject(DynamicFormHelper);
    private store = inject<Store<Record<string, DynamicFormState>>>(Store);
    private formStore = inject(DynamicFormStore);
    private elemRef = inject(ElementRef);
    private cd = inject(ChangeDetectorRef);

    // Static
    static HOST_CLASS = 'aix-flex-grid__col';

    // Decorators
    @HostBinding('class')
    classNames = AixDynamicInputComponent.HOST_CLASS;

    @HostBinding('attr.aix-control')
    aixControl: string;

    // Store Data
    templateData = {
        valueToRender: '',
        fieldIsDisabled: false,
        fieldIsRequired: false,
        fieldIsDirty: false,
        ctrlIsInvalid: false,
        ctrlHasRequiredError: false,
        canDisplayErrors: false,
        ctrlErrors: null as ValidationErrors | null
    };
    userInputEmitter$ = new Subject<string>();
    debouncer = new Subject<string>();

    // Other
    config: InputFieldConfig;
    private subscriptions: Subscription[] = [];
    private isTyping = false;

    caretPositionStart: number;
    caretPositionEnd: number;
    keyDownEvent: KeyboardEvent;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.cd.detach();
    }

    ngOnInit() {
        const formUID = this.formStore.formUID;
        const refId = this.config.refId as string;
        this.aixControl = refId;
        this.classNames = this.calculateClassNames();

        // Control initialization
        this.formStore.addControl(this.config);

        // Store Control Observables
        const ctrlStore$ = this.formStore.getControlStoreByRefId(refId);
        const valueToRender$ = this.formStore.getDataStore<string>(
            getDataPathByFieldConfig(this.config)
        );

        this.subscriptions.push(
            ctrlStore$
                .pipe(auditTime(5), withLatestFrom(valueToRender$))
                .subscribe(([ctrl, valueToRender]) => {
                    // valueToRender
                    if (!this.isTyping) {
                        this.valueToSend(valueToRender);
                    }

                    if (ctrl.configUpdateByRelation) {
                        this.config = {
                            ...(ctrl.fieldConfig as InputFieldConfig)
                        };
                    }

                    // fieldIsDisabled
                    this.templateData.fieldIsDisabled =
                        ctrl.disabledByRelation || ctrl.fieldConfig.disabled === true;

                    // fieldIsRequired
                    const hasRequiredValidator = (
                        ctrl.fieldConfig.validation as ValidatorFn[]
                    ).some(val => val === Validators.required);
                    this.templateData.fieldIsRequired =
                        ctrl.requiredByRelation || hasRequiredValidator;

                    // fieldIsDirty
                    this.templateData.fieldIsDirty = ctrl.isDirty;

                    // ctrlErrors
                    this.templateData.ctrlErrors = ctrl.validation;

                    // ctrlHasRequiredError
                    this.templateData.ctrlHasRequiredError =
                        ctrl.validation && ctrl.validation['required'];

                    // ctrlIsInvalid
                    this.templateData.ctrlIsInvalid = ctrl.validation !== null;

                    // canDisplayErrors
                    const hasNonRequiredErrors =
                        !!ctrl.validation &&
                        Object.keys(ctrl.validation).some(key => key !== 'required');
                    this.templateData.canDisplayErrors =
                        (ctrl.validation !== null && ctrl.isDirty) || hasNonRequiredErrors;

                    // Server Validation errors
                    handleServerValdationErrors(ctrl, evt =>
                        (this.elemRef.nativeElement as HTMLElement).dispatchEvent(evt)
                    );

                    this.cd.detectChanges();

                    this.isTyping = false;
                })
        );

        // User input
        this.subscriptions.push(
            this.userInputEmitter$.subscribe(value => {
                this.store.dispatch(
                    new DynamicFormSetDataAction(
                        formUID,
                        getDataPathByFieldConfig(this.config),
                        value
                    )
                );
                this.setDirty();

                // Store Actions
                this.helper.dispatchStoreActions(this.store, this.config, value);
            }),
            this.debouncer
                .pipe(debounceTime(this.helper.INPUT_DEBOUNCE))
                .subscribe(value => this.userInputEmitter$.next(value))
        );

        // Notifications
        this.subscriptions.push(
            combineLatest([valueToRender$.pipe(first()), this.userInputEmitter$])
                .pipe(
                    filter(
                        ([defaultValue, userInput]) =>
                            stringIsNotEmpty(defaultValue) && defaultValue !== userInput
                    )
                )
                .subscribe(_ => {
                    const aixEvt = new CustomEvent(FieldEvent.CHANGE, {
                        detail: this.config,
                        bubbles: true,
                        cancelable: true
                    });
                    this.elemRef.nativeElement.dispatchEvent(aixEvt);
                })
        );

        // On Load Store Actions
        this.subscriptions.push(
            valueToRender$
                .pipe(first())
                .subscribe(value =>
                    this.helper.dispatchOnLoadStoreActions(this.store, this.config, value)
                )
        );

        this.cd.detectChanges();
    }

    valueChange(value: string) {
        this.isTyping = true;
        // this.templateData.valueToRender = value;
        this.debouncer.next(value);
    }

    setDirty() {
        this.store.dispatch(
            new DynamicFormControlSetDirtyAction(
                this.formStore.formUID,
                this.config.refId as string
            )
        );
    }

    valueToSend(rawValue: string) {
        this.templateData.valueToRender = rawValue;
    }

    private calculateClassNames() {
        let classNames = this.config.classNames
            ? [
                  AixDynamicInputComponent.HOST_CLASS,
                  ...this.helper.parseHostProperties(this.config.classNames.host)
              ].join(' ')
            : AixDynamicInputComponent.HOST_CLASS;
        classNames = this.config.hidden
            ? classNames.concat(` ${getHostPropertyClass().hidden}`)
            : classNames;
        return classNames;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.formStore.removeControl(this.config);
    }
}
